import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";
import { Auth0Provider } from "@auth0/auth0-react";
import { HelmetProvider } from "react-helmet-async";

// ── Redirect loop auto-heal (legacy /hr cache) ──
// Some users are stuck in a reload loop between "/" and "/hr" caused by an
// OLD service-worker that pre-caches HTML/JS redirecting "/" → "/hr". If the
// same page reloads more than 3 times within 5 seconds, forcibly unregister
// every SW, wipe all caches and local storage, then reload once — this
// guarantees the fresh bundle (which only strips "/hr", never adds it) takes
// over on the very next paint.
try {
  const LOOP_WINDOW_MS = 5000;
  const LOOP_MAX = 3;
  const now = Date.now();
  const last = parseInt(sessionStorage.getItem("emina_nav_ts") || "0", 10);
  let count = parseInt(sessionStorage.getItem("emina_nav_count") || "0", 10);
  count = now - last < LOOP_WINDOW_MS ? count + 1 : 1;
  sessionStorage.setItem("emina_nav_ts", String(now));
  sessionStorage.setItem("emina_nav_count", String(count));
  if (count > LOOP_MAX && !sessionStorage.getItem("emina_loop_healed")) {
    sessionStorage.setItem("emina_loop_healed", "1");
    (async () => {
      try {
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch (_) { /* ignore */ }
      try {
        // Preserve SW version marker and language preference so the healed
        // session does not re-trigger a bunch of silent auth / i18n flips.
        const lang = localStorage.getItem("emina_locale");
        localStorage.clear();
        if (lang) localStorage.setItem("emina_locale", lang);
      } catch (_) { /* ignore */ }
      // Always land on "/" (never "/hr") to break out of the loop.
      window.location.replace("/");
    })();
  }
} catch (_) { /* ignore */ }

// ── Legacy /hr locale redirect ──
// Any traffic hitting /hr or /hr/... must be permanently redirected to the
// equivalent path without the prefix (/hr/clanak/foo → /clanak/foo). Runs
// synchronously before React mounts so Google/PWA share URLs never settle
// on the stale locale path.
try {
  const _path = window.location.pathname || "/";
  if (_path === "/hr" || _path === "/hr/" || _path.startsWith("/hr/")) {
    const stripped = _path === "/hr" || _path === "/hr/" ? "/" : _path.slice(3);
    const target = stripped + window.location.search + window.location.hash;
    window.location.replace(target);
  }
} catch (_) { /* ignore */ }

const domain = process.env.REACT_APP_AUTH0_DOMAIN || "";
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || "";
const audience = process.env.REACT_APP_AUTH0_AUDIENCE || "";
const redirectUri = window.location.origin + "/callback";

// Expose configuration state so hooks can short-circuit Auth0 redirects when
// the tenant is not configured (prevents Chrome "Unsafe attempt to load URL
// https://authorize/" errors from `loginWithRedirect` composing an /authorize
// URL with empty domain/client_id).
const hasAuth0 = Boolean(domain && clientId);
window.__EMINA_AUTH0_CONFIGURED__ = hasAuth0;

// ── Stale Auth0 state guard ──────────────────────────────────────────────
// When a user has a valid LOCAL admin JWT, or when they're on the local
// /admin/login page, we must prevent Auth0's silent-auth iframe from firing.
// A broken or misconfigured Auth0 tenant causes the iframe to postMessage()
// an un-cloneable Request object back to the parent window, producing:
//   "Failed to execute 'postMessage' on 'Window': Request object could not be cloned"
// Wiping any stale `@@auth0spajs@@:*` localStorage keys disables checkSession().
try {
  const hasLocal = !!localStorage.getItem('emina_local_admin_token');
  const onLogin = window.location.pathname.startsWith('/admin/login');
  if (hasLocal || onLogin) {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('@@auth0spajs@@')) localStorage.removeItem(k);
    });
  }
} catch (_) { /* localStorage unavailable — ignore */ }

// ── Global error muffler for the Auth0 silent-auth iframe ────────────────
// When the Auth0 tenant is misconfigured (e.g. missing Allowed Web Origin
// for the current host), the SDK's internal iframe posts a Request object
// back to the parent which the structured-clone algorithm rejects. The error
// surfaces as an unhandled rejection / window error but the page is
// otherwise fine. Suppress the noise so it never reaches Sentry / console.
try {
  const SILENT_PATTERNS = [
    "Request object could not be cloned",
    "Failed to execute 'postMessage' on 'Window'",
  ];
  const isSilent = (msg) => typeof msg === 'string' && SILENT_PATTERNS.some((p) => msg.includes(p));
  window.addEventListener('error', (e) => {
    if (isSilent(e?.message)) { e.preventDefault(); e.stopImmediatePropagation(); return false; }
  }, true);
  window.addEventListener('unhandledrejection', (e) => {
    const m = e?.reason?.message || String(e?.reason || '');
    if (isSilent(m)) { e.preventDefault(); }
  });
} catch (_) { /* ignore */ }

// Always mount Auth0Provider (so `useAuth0` never throws), but pass a benign
// placeholder domain when no real tenant is configured. Consumers must gate
// `loginWithRedirect` on `window.__EMINA_AUTH0_CONFIGURED__`.
const safeDomain = domain || "auth0.invalid";
const safeClientId = clientId || "disabled";

// Skip the SPA SDK's automatic "?code=... → handleRedirectCallback" parsing on
// every route except the designated /callback URL. Otherwise the Provider will
// attempt to consume query params on unrelated pages and can surface opaque
// postMessage errors when the iframe-driven silent auth fails.
const onCallback = window.location.pathname.startsWith('/callback');

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Auth0Provider
        domain={safeDomain}
        clientId={safeClientId}
        cacheLocation="memory"
        useRefreshTokens={false}
        skipRedirectCallback={!onCallback}
        authorizationParams={{
          redirect_uri: redirectUri,
          audience: audience || undefined,
        }}
      >
        <App />
      </Auth0Provider>
    </HelmetProvider>
  </React.StrictMode>
);

// ── PWA service worker registration (offline-first + push notifications) ──
// One-time migration: nuke stale v<N-1> caches/registrations before installing v<N>.
// Bump this on every change that could leave users stuck on a cached redirect /
// routing chain (e.g. reports of `/hr/` being appended on load).
const SW_VERSION = 'v8';
if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
  window.addEventListener('load', async () => {
    try {
      const prev = localStorage.getItem('emina_sw_version');
      if (prev !== SW_VERSION) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        localStorage.setItem('emina_sw_version', SW_VERSION);
      }
      await navigator.serviceWorker.register('/sw.js');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Service worker init failed:', err);
    }
  });
}
